import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useGetPredictionTechsQuery } from '@/app/services/equipment';
import { SearchableSelect } from '@/components/common/searchable-select';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import {
  collectVersions,
  ON_FARM_IMPLANT_THAW_METHODS,
  OnFarmImplantRequestError,
  type OnFarmImplantSessionEmbryo,
  useRecordOnFarmImplantThawDiscard,
} from '@/services/on-farm-implant';

type ThawAndDiscardDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  embryos: OnFarmImplantSessionEmbryo[];
  onDiscarded: () => void;
};

export const ThawAndDiscardDialog = ({
  open,
  onOpenChange,
  sessionId,
  embryos,
  onDiscarded,
}: ThawAndDiscardDialogProps) => {
  const { t } = useTranslation();

  const [thawMethodId, setThawMethodId] = useState('');
  const [thawTechId, setThawTechId] = useState('');
  const [thawThermosTemp, setThawThermosTemp] = useState('');
  const [problems, setProblems] = useState<string[]>([]);

  const { mutateAsync: recordThawDiscard, isPending } =
    useRecordOnFarmImplantThawDiscard();

  const { data: techsData } = useGetPredictionTechsQuery({
    where: {
      or: [{ farmlocation: { eq: false } }, { farmlocation: { eq: null } }],
    },
  });

  const techOptions = useMemo(
    () =>
      (techsData ?? []).map((tech) => ({
        label: tech.name ?? '',
        value: tech.id ?? '',
      })),
    [techsData],
  );

  const thawMethodOptions = useMemo(
    () =>
      ON_FARM_IMPLANT_THAW_METHODS.map((method) => ({
        value: String(method.value),
        label: t(`onFarmImplant.frozen.thawDiscard.methods.${method.labelKey}`),
      })),
    [t],
  );

  const detailsMissing = !thawMethodId || !thawTechId;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setThawMethodId('');
        setThawTechId('');
        setThawThermosTemp('');
        setProblems([]);
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const handleConfirm = useCallback(async () => {
    if (embryos.length === 0 || detailsMissing) return;

    setProblems([]);

    try {
      const result = await recordThawDiscard({
        sessionId,
        input: {
          items: embryos.map((embryo) => ({
            sessionEmbryoId: embryo.sessionEmbryoId,
          })),
          thawMethodId: Number(thawMethodId),
          thawTechId,
          thawThermosTemp: thawThermosTemp.trim() || null,
          versions: collectVersions(embryos),
        },
      });

      handleOpenChange(false);
      onDiscarded();

      toast.add({
        data: { variant: 'success' },
        title: t('onFarmImplant.frozen.thawDiscard.success', {
          count: result.totalDiscarded,
        }),
      });
    } catch (error) {
      if (error instanceof OnFarmImplantRequestError && error.problems.length) {
        setProblems(error.problems);
        return;
      }

      console.error('Failed to record the thaw and discard:', error);
      toast.add({
        data: { variant: 'error' },
        title: t('onFarmImplant.frozen.thawDiscard.failed'),
      });
    }
  }, [
    detailsMissing,
    embryos,
    handleOpenChange,
    onDiscarded,
    recordThawDiscard,
    sessionId,
    t,
    thawMethodId,
    thawTechId,
    thawThermosTemp,
  ]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="md:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t('onFarmImplant.frozen.thawDiscard.title')}
          </DialogTitle>
          <DialogDescription>
            {t('onFarmImplant.frozen.thawDiscard.description')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          {problems.length > 0 && (
            <div
              role="alert"
              className="rounded-md bg-red-50 p-3 text-xs text-red-700"
            >
              <ul className="list-inside list-disc">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs" htmlFor="thaw-discard-method">
                {t('onFarmImplant.frozen.thawDiscard.thawMethod')}
              </Label>
              <Select
                items={thawMethodOptions}
                value={thawMethodId}
                onChange={(value) => setThawMethodId((value as string) ?? '')}
              >
                <SelectTrigger
                  id="thaw-discard-method"
                  className="h-8 w-full text-xs"
                  aria-invalid={!thawMethodId}
                >
                  <SelectValue
                    placeholder={t(
                      'onFarmImplant.frozen.thawDiscard.selectThawMethod',
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {thawMethodOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-xs" htmlFor="thaw-discard-tech">
                {t('onFarmImplant.frozen.thawDiscard.thawTech')}
              </Label>
              <SearchableSelect
                className="h-8 w-full text-xs"
                options={techOptions}
                value={thawTechId}
                onChange={setThawTechId}
                placeholder={t(
                  'onFarmImplant.frozen.thawDiscard.selectThawTech',
                )}
                emptyMessage={t('onFarmImplant.frozen.thawDiscard.noTechs')}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-xs" htmlFor="thaw-discard-temp">
                {t('onFarmImplant.frozen.thawDiscard.thermosTemp')}
              </Label>
              <Input
                id="thaw-discard-temp"
                className="h-8 text-xs"
                value={thawThermosTemp}
                onChange={(event) => setThawThermosTemp(event.target.value)}
              />
            </div>
          </div>

          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-gray-500">
                <th className="py-1 font-medium">
                  {t('onFarmImplant.frozen.embryo')}
                </th>
                <th className="py-1 font-medium">
                  {t('onFarmImplant.frozen.frozenStraw')}
                </th>
                <th className="py-1 font-medium">
                  {t('onFarmImplant.frozen.cane')}
                </th>
              </tr>
            </thead>
            <tbody>
              {embryos.map((embryo) => (
                <tr key={embryo.sessionEmbryoId} className="border-t">
                  <td className="py-1.5">{embryo.embryoName ?? '—'}</td>
                  <td className="py-1.5">{embryo.frozenStrawNumber ?? '—'}</td>
                  <td className="py-1.5">{embryo.cane ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {detailsMissing && (
            <p className="text-xs text-red-600">
              {t('onFarmImplant.frozen.thawDiscard.detailsRequired')}
            </p>
          )}

          <p className="text-xs text-gray-500">
            {t('onFarmImplant.frozen.thawDiscard.permanentWarning')}
          </p>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={embryos.length === 0 || detailsMissing || isPending}
          >
            {isPending && <i className="pi pi-spin pi-spinner" />}
            {t('onFarmImplant.frozen.thawDiscard.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
